import React, { useState, useMemo, useEffect } from 'react';
import {
  Tab,
  Tabs,
  Grid,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import Box from '../single_component/Box';
import DocumentBox from '../common_component/DocumentBox';
import theme from '../../utils/Theme';
import Props from '../../types/props';

type Category = 'constitutional' | 'kyc' | 'financial' | 'other' | 'promoter';

const CATEGORIES: Category[] = [
  'constitutional',
  'kyc',
  'financial',
  'other',
  'promoter',
];

const ALL_TAB_LABELS = [
  'Constitutional Documents',
  'KYC Documents',
  'Financial Documents',
  'Other Documents',
  'Promoter Documents',
];

const EXISTING_CATS = new Set<string>([
  'constitutional',
  'kyc',
  'financial',
  'promoter',
]);

function ViewSpvTabDocuments({ ...props }: Props) {
  const projectData = props?.extra?.document ?? {};
  const existingDocs: any[] = projectData?.documents ?? [];

  const hasTabName = props.extra.tabName !== undefined;
  const tabOffset = hasTabName ? 1 : 0;

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [folderSearch, setFolderSearch] = useState('');
  const [preservedFolders, setPreservedFolders] = useState<
    Record<string, string[]>
  >({
    constitutional: [],
    kyc: [],
    financial: [],
    other: [],
    promoter: [],
  });

  const activeCategory: Category = CATEGORIES[selectedTab + tabOffset];

  const existingByCategory = useMemo(() => {
    const result: Record<string, Record<string, any[]>> = {
      constitutional: {},
      kyc: {},
      financial: {},
      other: {},
      promoter: {},
    };
    existingDocs.forEach((doc: any) => {
      const cat: Category = EXISTING_CATS.has(doc.category)
        ? (doc.category as Category)
        : 'other';
      const folderName: string = doc.folderName ?? 'General';
      if (!result[cat][folderName]) result[cat][folderName] = [];
      result[cat][folderName].push(doc);
    });
    return result;
  }, [existingDocs]);

  useEffect(() => {
    setPreservedFolders(prev => {
      const updated: Record<string, string[]> = { ...prev };
      existingDocs.forEach((doc: any) => {
        const cat: string = EXISTING_CATS.has(doc.category)
          ? doc.category
          : 'other';
        const fn: string = doc.folderName ?? 'General';
        if (!updated[cat]) updated[cat] = [];
        if (!updated[cat].includes(fn)) {
          updated[cat] = [...updated[cat], fn];
        }
      });
      return updated;
    });
  }, [existingDocs]);

  const allFolderNames = useMemo(() => {
    const existing = Object.keys(existingByCategory[activeCategory] ?? {});
    const preserved = preservedFolders[activeCategory] ?? [];
    return Array.from(new Set([...existing, ...preserved]));
  }, [activeCategory, existingByCategory, preservedFolders]);

  const filteredFolderNames = useMemo(() => {
    const q = folderSearch.trim().toLowerCase();
    if (!q) return allFolderNames;
    return allFolderNames.filter(n => n.toLowerCase().includes(q));
  }, [allFolderNames, folderSearch]);

  const docsInSelectedFolder = useMemo(
    () =>
      selectedFolder
        ? (existingByCategory[activeCategory][selectedFolder] ?? [])
        : [],
    [selectedFolder, activeCategory, existingByCategory]
  );

  const handleTabChange = (_event: any, v: number) => {
    setSelectedTab(v);
    setSelectedFolder(null);
    setFolderSearch('');
  };

  const tabSx = {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.primary.main,
    textTransform: 'none' as const,
  };

  const visibleTabLabels = hasTabName
    ? ALL_TAB_LABELS.slice(1)
    : ALL_TAB_LABELS;

  return (
    <Box>
      <Box
        {...props}
        style={{
          backgroundColor: 'white',
          boxShadow: '0px 2px 10px 0px #10496030',
          borderRadius: 2,
          marginTop: 5,
        }}
      >
        <Box
          style={{
            backgroundColor: '#E5F7FF',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            centered
            sx={{
              paddingInline: 5,
              '& .MuiTabs-flexContainer': { justifyContent: 'space-between' },
              '& .MuiTabs-indicator': { background: '#25A9E0', height: 3 },
            }}
          >
            {visibleTabLabels.map(label => (
              <Tab key={label} label={label} sx={tabSx} />
            ))}
          </Tabs>
        </Box>

        <Grid
          container
          sx={{ minHeight: 420, maxHeight: 520, overflow: 'hidden' }}
        >
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              borderRight: '1px solid #E0E0E0',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 520,
            }}
          >
            <Box style={{ padding: '12px 16px 8px' }}>
              <TextField
                placeholder="Search folders..."
                value={folderSearch}
                onChange={e => setFolderSearch(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 1.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#757575' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: '#757575', fontWeight: 600 }}
              >
                FOLDERS ({filteredFolderNames.length})
              </Typography>
            </Box>

            <Divider />

            {filteredFolderNames.length === 0 ? (
              <Box
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                }}
              >
                <FolderIcon sx={{ fontSize: 40, mb: 1, color: '#BDBDBD' }} />
                <Typography
                  variant="body2"
                  sx={{ color: '#9E9E9E', textAlign: 'center' }}
                >
                  No folders found
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
                {filteredFolderNames.map(folderName => {
                  const count = (
                    existingByCategory[activeCategory][folderName] ?? []
                  ).length;
                  const isSelected = selectedFolder === folderName;
                  return (
                    <ListItemButton
                      key={folderName}
                      selected={isSelected}
                      onClick={() => setSelectedFolder(folderName)}
                      sx={{
                        borderRadius: 0,
                        '&.Mui-selected': { backgroundColor: '#E5F7FF' },
                        '&.Mui-selected:hover': { backgroundColor: '#d4f0fb' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {isSelected ? (
                          <FolderOpenIcon sx={{ color: '#25A9E0' }} />
                        ) : (
                          <FolderIcon
                            sx={{ color: theme.palette.primary.main }}
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: 13,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {folderName}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{ color: '#9E9E9E' }}
                          >
                            {count} {count === 1 ? 'document' : 'documents'}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Grid>

          <Grid item xs={12} md={8} sx={{ maxHeight: 520, overflowY: 'auto' }}>
            {selectedFolder === null ? (
              <Box
                style={{
                  height: '100%',
                  minHeight: 380,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InsertDriveFileIcon
                  sx={{ fontSize: 48, mb: 1.5, color: '#BDBDBD' }}
                />
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, color: '#757575' }}
                >
                  No folder selected
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#BDBDBD', mt: 0.5 }}
                >
                  Select a folder from the left panel
                </Typography>
              </Box>
            ) : (
              <Box style={{ padding: '16px 24px' }}>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 2,
                  }}
                >
                  <FolderOpenIcon sx={{ color: '#25A9E0', fontSize: 22 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: 15 }}
                  >
                    {selectedFolder}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {docsInSelectedFolder.length === 0 ? (
                  <Box
                    style={{ textAlign: 'center', padding: '24px 0 8px' }}
                  >
                    <InsertDriveFileIcon
                      sx={{ fontSize: 36, color: '#BDBDBD', mb: 1 }}
                    />
                    <Typography variant="body2" sx={{ color: '#9E9E9E' }}>
                      No documents in this folder
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    style={{
                      overflowY: 'auto',
                      maxHeight: '40vh',
                      paddingRight: 1,
                    }}
                  >
                    {docsInSelectedFolder.map((doc: any) => (
                      <Box key={doc.documentId} style={{ marginBottom: 0 }}>
                        <DocumentBox
                          extra={{
                            url: doc.documentFile,
                            textlabel: doc.documentName,
                            chiplabel: doc.fileName ?? doc.documentName,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default ViewSpvTabDocuments;
